import { lazy, Suspense } from "react";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { LanguageProvider } from "@/contexts/LanguageContext";
import PublicLayout from "@/components/layout/PublicLayout";
import Index from "./pages/Index";

// Lazy-load non-critical UI that isn't needed for first paint
const Toaster = lazy(() => import("@/components/ui/toaster").then(m => ({ default: m.Toaster })));
const Sonner = lazy(() => import("@/components/ui/sonner").then(m => ({ default: m.Toaster })));
const CookieConsent = lazy(() => import("./components/CookieConsent"));
const BackToTop = lazy(() => import("./components/BackToTop"));

// Lazy-loaded routes
const NotFound = lazy(() => import("./pages/NotFound"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const AdminLogin = lazy(() => import("./pages/AdminLogin"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const ProtectedRoute = lazy(() => import("./components/ProtectedRoute"));
const AboutUs = lazy(() => import("./pages/AboutUs"));
const Testimonials = lazy(() => import("./pages/Testimonials"));
const UsersClients = lazy(() => import("./pages/UsersClients"));
const Partners = lazy(() => import("./pages/Partners"));
const Offers = lazy(() => import("./pages/Offers"));
const SolutionsPage = lazy(() => import("./pages/Solutions"));
const SpotWeldingSinks = lazy(() => import("./pages/solutions/SpotWeldingSinks"));
const SeamWeldingPillowPlates = lazy(() => import("./pages/solutions/SeamWeldingPillowPlates"));
const RoboticSpotWeldingGuns = lazy(() => import("./pages/solutions/RoboticSpotWeldingGuns"));
const CompactingTools = lazy(() => import("./pages/solutions/CompactingTools"));
const LockWelding = lazy(() => import("./pages/solutions/LockWelding"));
const GastronomyMedicine = lazy(() => import("./pages/solutions/GastronomyMedicine"));
const IndustrialPedestalWelders = lazy(() => import("./pages/products/IndustrialPedestalWelders"));
const MultiSpotWeldingMachine = lazy(() => import("./pages/products/MultiSpotWeldingMachine"));
const IndustrialProjectionWelders = lazy(() => import("./pages/products/IndustrialProjectionWelders"));
const IndustrialSeamWelders = lazy(() => import("./pages/products/IndustrialSeamWelders"));
const SeamWelderRK94 = lazy(() => import("./pages/products/SeamWelderRK94"));
const WeldingHeads = lazy(() => import("./pages/products/WeldingHeads"));
const WeldingPincers = lazy(() => import("./pages/products/WeldingPincers"));
const BatteryProcessing = lazy(() => import("./pages/products/BatteryProcessing"));
const Workstation = lazy(() => import("./pages/products/Workstation"));
const SpotWelding = lazy(() => import("./pages/products/SpotWelding"));
const CarSpotters = lazy(() => import("./pages/products/CarSpotters"));
const MicroWelding = lazy(() => import("./pages/products/MicroWelding"));
const SpotWeldingDetail = lazy(() => import("./pages/products/spot-welding/SpotWeldingDetail"));
const ButtWelding = lazy(() => import("./pages/products/ButtWelding"));
const ProjectionWelding = lazy(() => import("./pages/products/ProjectionWelding"));
const KnowledgeHub = lazy(() => import("./pages/KnowledgeHub"));
const NewsArticlePage = lazy(() => import("./pages/NewsArticle"));
const FAQPage = lazy(() => import("./pages/FAQPage"));
const Services = lazy(() => import("./pages/Services"));
const SpareParts = lazy(() => import("./pages/SpareParts"));
const TipDressers = lazy(() => import("./pages/accessories/TipDressers"));
const BoschRexroth = lazy(() => import("./pages/spare-parts/BoschRexroth"));
const HarmsWende = lazy(() => import("./pages/spare-parts/HarmsWende"));
const Consumables = lazy(() => import("./pages/Consumables"));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <LanguageProvider>
      <TooltipProvider>
        <Suspense fallback={null}>
          <Toaster />
          <Sonner />
        </Suspense>
        <BrowserRouter>
          <Suspense fallback={null}>
            <CookieConsent />
            <BackToTop />
          </Suspense>
          <Suspense fallback={null}>
            <Routes>
              <Route element={<PublicLayout />}>
                <Route path="/" element={<Index />} />
                <Route path="/about-us" element={<AboutUs />} />
                <Route path="/knowledge-hub" element={<KnowledgeHub />} />
                <Route path="/news/:slug" element={<NewsArticlePage />} />
                <Route path="/testimonials" element={<Testimonials />} />
                <Route path="/users-clients" element={<UsersClients />} />
                <Route path="/partners" element={<Partners />} />
                <Route path="/products" element={<Offers />} />
                <Route path="/solutions" element={<SolutionsPage />} />
                <Route path="/solutions/gastronomy-medicine" element={<GastronomyMedicine />} />
                <Route path="/solutions/spot-welding-sinks" element={<SpotWeldingSinks />} />
                <Route path="/solutions/seam-welding-pillow-plates" element={<SeamWeldingPillowPlates />} />
                <Route path="/solutions/robotic-spot-welding-guns" element={<RoboticSpotWeldingGuns />} />
                <Route path="/solutions/compacting-tools" element={<CompactingTools />} />
                <Route path="/solutions/lock-welding" element={<LockWelding />} />
                <Route path="/products/industrial-pedestal-welders" element={<IndustrialPedestalWelders />} />
                <Route path="/products/multi-spot-welding-machine" element={<MultiSpotWeldingMachine />} />
                <Route path="/products/industrial-projection-welders" element={<IndustrialProjectionWelders />} />
                <Route path="/products/industrial-seam-welders" element={<IndustrialSeamWelders />} />
                <Route path="/products/seam-welding/rk-94" element={<SeamWelderRK94 />} />
                <Route path="/products/welding-heads" element={<WeldingHeads />} />
                <Route path="/products/welding-pincers" element={<WeldingPincers />} />
                <Route path="/products/battery-processing" element={<BatteryProcessing />} />
                <Route path="/products/workstation" element={<Workstation />} />
                <Route path="/products/spot-welding" element={<SpotWelding />} />
                <Route path="/products/car-spotters" element={<CarSpotters />} />
                <Route path="/products/micro-welding" element={<MicroWelding />} />
                <Route path="/products/spot-welding/:machineId" element={<SpotWeldingDetail />} />
                <Route path="/products/butt-welding" element={<ButtWelding />} />
                <Route path="/products/projection-welding" element={<ProjectionWelding />} />
                <Route path="/faq" element={<FAQPage />} />
                <Route path="/services" element={<Services />} />
                <Route path="/spare-parts" element={<SpareParts />} />
                <Route path="/accessories/tip-dressers" element={<TipDressers />} />
                <Route path="/spare-parts/bosch-rexroth" element={<BoschRexroth />} />
                <Route path="/spare-parts/harms-wende" element={<HarmsWende />} />
                <Route path="/consumables" element={<Consumables />} />
                <Route path="/splosni-pogoji" element={<PrivacyPolicy />} />
              </Route>
              <Route path="/admin" element={<AdminLogin />} />
              <Route path="/admin/baza" element={<Suspense fallback={null}><ProtectedRoute><AdminDashboard /></ProtectedRoute></Suspense>} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </LanguageProvider>
  </QueryClientProvider>
);

export default App;
